import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useDarkMode } from '../DarkModeContext';

const LanguageSwitcher = ({ className = '', compact = false }) => {
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { darkMode } = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleLanguageChange = async (langCode) => {
    await changeLanguage(langCode);
    setIsOpen(false);
  };

  const currentLang = supportedLanguages.find(l => l.code === currentLanguage);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
          darkMode
            ? 'hover:bg-white/10 text-gray-300 hover:text-white'
            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
        }`}
        aria-label="Change language"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4" />
        {!compact && (
          <>
            <span>{currentLang?.flag} {currentLang?.nativeLabel || 'English'}</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-1 w-52 rounded-xl shadow-xl border z-[100] overflow-hidden ${
            darkMode
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Language
            </p>
          </div>

          <div className="py-1">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                  darkMode
                    ? 'hover:bg-gray-700/70 text-gray-200'
                    : 'hover:bg-gray-50 text-gray-700'
                } ${currentLanguage === lang.code
                    ? (darkMode ? 'bg-gray-700/50' : 'bg-blue-50')
                    : ''
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-base">{lang.flag}</span>
                  <div className="flex flex-col">
                    <span className="font-medium leading-tight">{lang.nativeLabel}</span>
                    <span className={`text-[11px] leading-tight ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {lang.label}
                    </span>
                  </div>
                </div>

                {currentLanguage === lang.code && (
                  <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
